import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import AddChipModal from '../components/AddChipModal';
import ChipListTable from '../components/ChipListTable';

interface Machine {
  id: number;
  number: string;
  isActive: boolean;
}

interface Chip {
  id: number;
  name: string;
  machineId: number;
  status: 'active' | 'inactive' | 'maintenance';
  lastSeen: string | null;
  isActive: boolean;
  chipNumber?: string | null;
  machine: Machine;
}

const ChipManagement: React.FC = () => {
  const { t } = useTranslation();
  const [chips, setChips] = useState<Chip[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingChip, setEditingChip] = useState<Chip | null>(null);
  const [error, setError] = useState('');

  // Statistics
  const [stats, setStats] = useState({
    totalChips: 0,
    activeChips: 0,
    onlinePercentage: 0
  });

  useEffect(() => {
    fetchChips();
  }, []);

  const fetchChips = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/chips');
      setChips(response.chips || []);
      
      // Calculate statistics
      const totalChips = response.chips?.length || 0;
      const activeChips = response.chips?.filter((chip: Chip) => chip.status === 'active').length || 0;
      const onlineChips = response.chips?.filter((chip: Chip) => {
        if (!chip.lastSeen) return false;
        const lastSeen = new Date(chip.lastSeen);
        const now = new Date();
        const diffInMinutes = (now.getTime() - lastSeen.getTime()) / (1000 * 60);
        return diffInMinutes <= 30; // Consider online if seen within 30 minutes
      }).length || 0;
      
      setStats({
        totalChips,
        activeChips,
        onlinePercentage: totalChips > 0 ? Math.round((onlineChips / totalChips) * 100) : 0
      });
    } catch (error) {
      console.error('Error fetching chips:', error);
      setError('Failed to fetch chips');
    } finally {
      setLoading(false);
    }
  };

  const handleChipAdded = () => {
    fetchChips();
  };

  const handleEditChip = (chip: Chip) => {
    setEditingChip(chip);
    setShowAddModal(true);
  };

  const handleDeleteChip = async (chipId: number) => {
    try {
      await apiService.delete(`/chips/${chipId}`);
      fetchChips();
    } catch (error) {
      console.error('Error deleting chip:', error);
      setError('Failed to delete chip');
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingChip(null);
  };

  return (
    <div className="p-6">
      {/* Floating Add Chip button */}
      <div className="fixed top-6 right-6 z-[1002]">
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white shadow-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105"
          title="Add Chip"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Chip Inventory Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Chip Inventory</h3>
              <p className="text-gray-600">Total Chips: {stats.totalChips}</p>
            </div>
          </div>
        </div>

        {/* Active Chips Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Active Chips</h3>
              <p className="text-gray-600">Currently Active: {stats.activeChips}</p>
            </div>
          </div>
        </div>

        {/* Tracking Status Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Tracking Status</h3>
              <p className="text-gray-600">Online: {stats.onlinePercentage}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chip List Table */}
      <ChipListTable
        chips={chips}
        onEditChip={handleEditChip}
        onDeleteChip={handleDeleteChip}
        loading={loading}
      />

      {/* Add/Edit Chip Modal */}
      <AddChipModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onChipAdded={handleChipAdded}
        editingChip={editingChip}
      />
    </div>
  );
};

export default ChipManagement;
